import React, { memo, useMemo } from "react"
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Loader2, Download, ChevronDown, Volume2, VolumeX } from "lucide-react"
import { useTranslation } from "react-i18next"
import { isBlockCode } from '../../../shared/utils/markdown'
import OfficialSourceNotice from '../../../shared/components/OfficialSourceNotice'
import type { OfficialSource } from '../../../shared/components/OfficialSourceNotice'
import SourceTranslation from './SourceTranslation'
import { isCantonCode } from '../../../shared/cantons'

/** One item of the `sources` frame; `[N]` in the answer is sources[N-1]. */
export interface Source {
  title: string
  // Italian label of the lane (fallback when `lane` is missing or unknown)
  source_type: string
  // Stable lane key, translated with t('lanes.<lane>')
  lane?: string
  source_id: string
  // Id of the row in its own table (for law_article, of the act)
  doc_id?: string
  // law_article | case_law_digest | doctrine | ti_court_decision | ch_court_decision |
  // firm_document | bank_statement
  doc_type?: string
  case_number?: string
  court_name?: string
  score: number
  content?: string
  article_number?: string
  doc_title?: string
  cited_verbatim?: boolean
  // 'in_vigore' | 'abrogato' | 'sconosciuto' (from the article's vector payload)
  legal_status?: string
  metadata?: { legal_status?: string; [key: string]: unknown }
  // Law texts imported from an official publisher (server legal_status/versions.py)
  official_source?: OfficialSource
  official_url?: string
  text_version_date?: string
  // Language of this source's text ('it' | 'de' | 'fr' | 'en' | …), empty when unknown
  language?: string
  // Law articles: 'CH' | 'INT' | a canton code ('TI', 'ZG', …)
  jurisdiction?: string
}

const isAbrogatedSource = (source: Source): boolean =>
  (source.legal_status ?? source.metadata?.legal_status) === 'abrogato'

/** Label of a source: its lane, translated; else the server's label. */
const sourceLaneLabel = (
  source: Source,
  translate: (key: string) => string,
  exists: (key: string) => boolean,
): string => {
  const key = source.lane ? `lanes.${source.lane}` : null
  if (key && exists(key)) return translate(key)
  return source.source_type || translate('chat.sources.fallbackLabel')
}

/** Canton of a cantonal-law source, when the answer cites another canton than
 * Ticino: many cantons share titles ("Steuergesetz"), so [3] and [5] would look
 * the same. While every cantonal source is Ticino's the label stays as it was. */
const cantonOfSource = (source: Source, sources: Source[]): string | null => {
  if (source.lane !== 'cantonal_law' || !isCantonCode(source.jurisdiction)) return null
  const mixed = sources.some((s) => s.lane === 'cantonal_law' && isCantonCode(s.jurisdiction) && s.jurisdiction !== 'TI')
  return mixed ? source.jurisdiction : null
}

export interface ReasoningStep {
  content: string
  step: string
  timestamp?: string
}

export interface DocumentDownload {
  download_url: string
  filename: string
}

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant' | 'system'
  reasoning?: ReasoningStep[]
  sources?: Source[]
  documentDownload?: DocumentDownload
  elapsedSeconds?: number
  // Language of the answer text ('it' | 'de' | 'fr' | 'en' | …), from the final_response frame
  answerLanguage?: string
}

export type TtsState = 'idle' | 'loading' | 'playing'

// Module-level constants: stable references so ReactMarkdown props don't
// change between renders.
export const REMARK_PLUGINS = [remarkGfm]

// Strip model-generated "Fonti:" section from the response text (sources are shown programmatically)
const stripSourcesSection = (text: string): string => {
  return text.replace(/\n+\*{0,2}Fonti:?\*{0,2}\n[\s\S]*$/i, '').trim()
}

// Preprocess markdown to turn [N] citation references into clickable anchors
const preprocessCitations = (text: string, hasSources: boolean): string => {
  if (!hasSources) return text
  // Replace [1], [2], etc. with clickable markdown links
  return text.replace(/\[(\d+)\]/g, (match, num) => {
    return `[${match}](#fonte-${num})`
  })
}

const USER_MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <p className="my-1 last:mb-0 text-white">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic text-white">{children}</em>,
  del: ({ children }) => <del className="line-through text-primary-200">{children}</del>,
  ul: ({ children }) => <ul className="list-disc list-inside my-2 text-white">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside my-2 text-white">{children}</ol>,
  li: ({ children }) => <li className="ml-2 text-white">{children}</li>,
  h1: ({ children }) => <h1 className="text-xl font-bold mt-3 mb-2 text-white">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-semibold mt-2 mb-1 text-white">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-medium mt-2 mb-1 text-white">{children}</h3>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary-300 pl-3 italic text-primary-100 my-2">{children}</blockquote>,
  // Block code is wrapped in <pre> by react-markdown itself; `code` only
  // styles the inner element (react-markdown 9 has no `inline` prop).
  pre: ({ children }) => <pre className="bg-primary-900 text-white p-2 rounded my-2 overflow-x-auto">{children}</pre>,
  code: ({ node, className, children, ...props }) => (
    isBlockCode(className, children)
      ? <code className={`text-sm font-mono ${className || ''}`} {...props}>{children}</code>
      : <code className="bg-primary-800 rounded px-1 py-0.5 text-white text-sm font-mono" {...props}>{children}</code>
  ),
  hr: () => <hr className="my-3 border-primary-400" />
}

const ASSISTANT_BASE_COMPONENTS: Components = {
  p: ({ children }) => <p className="my-3 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="text-xl font-bold mt-5 mb-3 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-2 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold mt-3 mb-1 first:mt-0">{children}</h4>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 my-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 my-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  hr: () => <hr className="my-4 border-gray-300 dark:border-gray-600" />,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary-200 pl-3 italic text-gray-600 dark:text-gray-400 my-3">{children}</blockquote>,
  table: ({ children }) => <div className="overflow-x-auto my-2"><table className="min-w-full text-xs border border-gray-300 dark:border-gray-600">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-700">{children}</thead>,
  th: ({ children }) => <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">{children}</td>,
  pre: ({ children }) => <pre className="bg-gray-800 text-white p-2 rounded my-2 overflow-x-auto">{children}</pre>,
  code: ({ node, className, children, style, ...props }) => (
    <code
      className={`${isBlockCode(className, children) ? 'font-mono text-sm' : 'bg-gray-200 dark:bg-gray-700 rounded px-1 py-0.5 font-mono text-sm'} ${className || ''}`}
      style={style}
      {...props}
    >
      {children}
    </code>
  ),
}

/** One reasoning step; memoized so only the step whose text changed re-parses. */
export const ReasoningStepView = memo(function ReasoningStepView({ content, streaming }: { content: string; streaming?: boolean }) {
  return (
    <div
      className={`text-xs text-gray-500 pl-4 border-l-2 border-primary-200 dark:border-primary-700 prose prose-xs max-w-none${streaming ? ' animate-pulse' : ''}`}
    >
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{content}</ReactMarkdown>
    </div>
  )
})

interface ChatMessageItemProps {
  message: Message
  ttsState: TtsState
  /** false hides the listen button (no local voice in this browser); default true */
  ttsAvailable?: boolean
  isDocLoading: boolean
  onTTS: (messageId: string, text: string) => void
  onSourceClick: (source: Source) => void
  onDownloadDocument: (download: DocumentDownload) => void
}

/**
 * A single chat message (reasoning, bubble, sources…). Memoized: while a new
 * answer streams, or while the user types, past messages don't re-render or
 * re-parse their markdown. All callbacks passed in must be stable.
 */
function ChatMessageItem({
  message,
  ttsState,
  ttsAvailable = true,
  isDocLoading,
  onTTS,
  onSourceClick,
  onDownloadDocument,
}: ChatMessageItemProps) {
  const { t, i18n } = useTranslation()
  const assistantComponents = useMemo<Components>(() => ({
    ...ASSISTANT_BASE_COMPONENTS,
    a: ({ href, children }) => {
      if (href && href.startsWith('#fonte-')) {
        const num = parseInt(href.replace('#fonte-', ''), 10)
        const source = message.sources && message.sources[num - 1]
        return (
          <button
            onClick={(e) => {
              e.preventDefault()
              if (source) {
                onSourceClick(source)
              }
            }}
            className="inline-flex items-center justify-center text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/50 rounded px-1 py-0 hover:bg-primary-100 dark:hover:bg-primary-800 cursor-pointer transition-colors"
            title={source ? t('chat.sources.open', { title: source.title }) : t('chat.sources.numbered', { n: num })}
          >
            {children}
          </button>
        )
      }
      return <span className="text-primary-600 font-medium">{children}</span>
    },
  }), [message.sources, onSourceClick, t])

  const assistantMarkdown = useMemo(
    () => message.role === 'assistant'
      ? preprocessCitations(stripSourcesSection(message.content), !!(message.sources && message.sources.length > 0))
      : '',
    [message.role, message.content, message.sources]
  )

  return (
    <div>
      {/* Reasoning steps (collapsible, before message) */}
      {message.reasoning && message.reasoning.length > 0 && (
        <details className="mb-1 ml-8">
          <summary className="flex items-center gap-1 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700 list-none [&::-webkit-details-marker]:hidden">
            <ChevronDown size={14} className="chevron-icon transition-transform duration-200" />
            {t('chat.message.reasoning', { count: message.reasoning.length })}
          </summary>
          <div className="mt-1 space-y-1">
            {message.reasoning.map((step, idx) => (
              <ReasoningStepView key={idx} content={step.content} />
            ))}
          </div>
        </details>
      )}

      {/* Main message */}
      <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[72%] rounded-md px-4 py-2 ${
            message.role === 'user'
              ? 'bg-primary-700 text-white'
              : 'bg-gray-50 border border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100'
          }`}
        >
          {message.role === 'user' ? (
            <ReactMarkdown
              className="prose prose-sm max-w-none prose-invert prose-p:my-0 prose-p:leading-relaxed"
              components={USER_MARKDOWN_COMPONENTS}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              className="prose prose-sm max-w-none text-gray-900 dark:text-gray-100 prose-p:leading-relaxed"
              components={assistantComponents}
            >
              {assistantMarkdown}
            </ReactMarkdown>
          )}
          {message.role === 'assistant' && ttsAvailable && (
            <div className="flex justify-end mt-1">
              <button
                onClick={() => onTTS(message.id, message.content)}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title={ttsState !== 'idle' ? t('chat.message.stopAudio') : t('chat.message.listen')}
              >
                {ttsState === 'loading' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : ttsState === 'playing' ? (
                  <VolumeX size={14} />
                ) : (
                  <Volume2 size={14} />
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Elapsed time */}
      {message.role === 'assistant' && message.elapsedSeconds != null && (
        <div className="ml-8 mt-0.5 text-[10px] text-gray-400">
          {message.elapsedSeconds < 60
            ? t('chat.elapsed.seconds', { s: message.elapsedSeconds })
            : t('chat.elapsed.minutes', { m: Math.floor(message.elapsedSeconds / 60), s: message.elapsedSeconds % 60 })
          }
        </div>
      )}

      {/* Document download button */}
      {message.documentDownload && (
        <div className="mt-2 ml-8">
          <button
            onClick={() => onDownloadDocument(message.documentDownload!)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={16} />
            {t('chat.message.downloadDocument', { filename: message.documentDownload.filename })}
          </button>
        </div>
      )}

      {/* Sources (collapsed by default) */}
      {message.sources && message.sources.length > 0 && (
        <details className="mt-2 ml-8">
          <summary className="flex items-center gap-1 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700 list-none [&::-webkit-details-marker]:hidden">
            <ChevronDown size={14} className="chevron-icon transition-transform duration-200" />
            {t('chat.sources.title', { n: message.sources.length })}
          </summary>
          <div className="mt-1 space-y-0.5">
            {message.sources.map((source, idx) => {
              const answerLanguage = message.answerLanguage || 'it'
              const foreign = !!source.language && source.language !== answerLanguage
              const laneLabel = sourceLaneLabel(source, (key) => t(key), (key) => i18n.exists(key))
              const canton = cantonOfSource(source, message.sources!)
              return (
                <div key={idx} id={`fonte-${message.id}-${idx + 1}`} className="text-xs text-primary-600 dark:text-primary-400 rounded px-1 -mx-1 transition-colors duration-500">
                  [{idx + 1}]{' '}
                  <span
                    title={source.source_type || undefined}
                    className="inline-block rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 text-[10px] px-1.5 py-0.5 mr-1 align-middle"
                  >
                    {canton ? t('chat.sources.laneWithCanton', { lane: laneLabel, canton: t(`cantons.${canton}`) }) : laneLabel}
                  </span>
                  <button
                    onClick={() => onSourceClick(source)}
                    className="underline hover:text-primary-800 dark:hover:text-primary-300 text-left"
                    disabled={isDocLoading}
                  >
                    {source.title}
                  </button>
                  {foreign && (
                    <span
                      className="inline-block rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[10px] px-1.5 py-0.5 ml-1 align-middle"
                      title={t('chat.sources.languageTitle')}
                    >
                      {t(`chat.sources.language.${source.language}`)}
                    </span>
                  )}
                  {foreign && source.content && (
                    <SourceTranslation text={source.content} targetLanguage={answerLanguage} />
                  )}
                  {source.case_number && ` (${source.case_number})`}
                  {source.court_name && ` — ${source.court_name}`}
                  {isAbrogatedSource(source) && (
                    <span
                      className="inline-block rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] px-1.5 py-0.5 ml-1 align-middle"
                      title={t('chat.sources.abrogatedTitle')}
                    >
                      {t('chat.sources.abrogated')}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          <OfficialSourceNotice className="mt-2" sources={message.sources.map((s) => s.official_source)} />
        </details>
      )}
    </div>
  )
}

export default memo(ChatMessageItem)
